import { IPatientSearchSchema } from './calendar';
import { IPatientSchema } from './patient';

export type IDispense = {
  id: number;
  dispense: string;
  name: string;
  email: string;
  status: DispenseOrderStatus;
  number: number;
  orderId: number;
  phone: number;
  patientId: number | string;
  axisId?: number;
  refractionId?: number;
  handoverNotes?: string;
  isExternal: boolean;
  voucherId: number;
  addonId: number;
  orderingNotes: string;
  totalPrice: number;
  patient: IPatientSchema; // contains firstName, date_of_birth, etc.
  specType?: string;
  pdRight?: number;
  pdLeft?: number;
  heightRight?: number;
  heightLeft?: number;
  bvd?: number;
  pantoscopicTilt?: number;
  wrapAngle?: number;
  pickupDateTime?: string;
  adjustedPrice?: number;
  discount?: number;
};

export type Dispense = {
  id: number;
  patientId: string;
  patientName: string;
  patientDOB: Date;
  lastDispenseStatus: DispenseOrderStatus;
  totalDispenses: number;
};

export type IDispenseRecordSchema = { type: string; date: Date };

export type IDispenseRequest = {
  specType: string;
  status: string; // restricts to allowed values
  type: string;
  patientId: number | string | null; // instead of `any`
  vouchers: any; // can refine later
  addons: any; // can refine later
  framesAndLenses: IFramesAndLenses[];
  orderingNotes?: string;
  handoverNotes?: string;
  sourceType?: string;
  sourceId?: number;
  pdRight?: number | null;
  pdLeft?: number | null;
  heightRight?: number | null;
  heightLeft?: number | null;
  bvd?: number | null;
  pantoscopicTilt?: number | null;
  wrapAngle?: number | null;
  pickupDateTime?: string;
  patient: IPatientSearchSchema;
  adjustedPrice?: number;
  totalPrice?: number;
  discount?: number;
  hobbies?: string;
  isVDU?: boolean;
  isSmoker?: boolean;
  isDriver?: boolean;
  isExternal?: boolean;
};

export type IFramesAndLenses = {
  id?: string;
  productId?: number;
  category: string;
  quantity: number;
  price: number;
  type: string;
  name: string;
  code: string;
  differentRL: boolean;
  rLens?: string;
  lLens?: string;
  goodsInId?: string;
  batchNumber?: string;
  packSize?: number;
  quantityIssued?: number;
  requiresLotTracking?: boolean;
};

export type IDispenseRequestFromHandover = {
  status: string;
  axisId: number;
  refractionId: number;
  handoverId: number;
  clinicalRecordId: number;
};

export type statusOptionsProps = {
  label: string;
  value: DispenseOrderStatus;
};

export type IRepairPart = {
  id: string;
  code?: string;
  sku?: string;
  name: string;
  currentStock: number;
  salePrice: number;
};

export type IRepairPartItem = {
  partId: string;
  quantity: number;
};

export type IRepairOrderRequest = {
  patientId: string | number;
  originalDispenseId?: number;
  repairType: string;
  parts: IRepairPartItem[];
  warranty: boolean;
};

export type IRepairOrder = {
  id: string;
  patientId: string | number;
  originalDispenseId?: number;
  repairType: string;
  partsSummary: string;
  cost: number;
  warranty: boolean;
  createdByUser?: any;
  createdDate: string;
  patient?: IPatientSchema;
  originalDispense?: IDispense;
};

export enum DispenseOrderStatus {
  DRAFT = 'draft',
  UNCANCELLED = 'uncancelled',
  UNPROCESSED = 'unprocessed',
  OUTSTANDING = 'outstanding',
  OVERDUE = 'overdue',
  UNCOLLECTED = 'uncollected',
}

// ── Sales (Dispense = source of truth for sales activity) ────────────────────
// These mirror the backend `SalesLedgerService` responses. Sales figures come
// from the Dispense module only; bank activity stays in the Finance module.

export type ISalesPeriod = { dateFrom: string; dateTo: string };

export type ISalesQuery = {
  dateFrom?: string;
  dateTo?: string;
  tillId?: number;
  patientId?: string;
  productId?: string;
  productType?: string;
  status?: string;
};

export type ISalesGroupBy =
  'product' | 'productType' | 'vatCategory' | 'month' | 'day';

export type ISalesPaymentTotals = {
  totalReceived: number;
  totalRefunded: number;
  netReceived: number;
  paymentCount: number;
  refundCount: number;
};

export type ISalesSummary = {
  period: ISalesPeriod;
  sales: {
    saleCount: number;
    /** Sales carrying a spec type — pairs of spectacles dispensed. */
    spectaclesDispensed: number;
    grossSales: number;
    netSales: number;
    vatAmount: number;
    exemptAmount: number;
  };
  items: {
    unitsSold: number;
    lineCount: number;
    distinctProducts: number;
    grossValue: number;
    netValue: number;
    vatAmount: number;
    exemptAmount: number;
  };
  payments: ISalesPaymentTotals;
};

/** Sales figures for one funding source, on the same basis as ISalesSummary. */
export type ISalesFundingSide = {
  saleCount: number;
  grossSales: number;
  netSales: number;
  vatAmount: number;
  exemptAmount: number;
  /** Sales with no VAT apportionment yet, so excluded from net/VAT/exempt. */
  salesAwaitingApportionment: number;
};

/** One NHS voucher band (A–J) and what was sold against it. */
export type ISalesFundingBand = {
  band: string;
  saleCount: number;
  grossSales: number;
  voucherValue: number;
};

/**
 * NHS versus private split of the period's sales.
 *
 * A sale is NHS when an NHS voucher band was applied to the dispense, and
 * private otherwise. The voucher does not reduce turnover — it decides who
 * settles it — so `nhs.grossSales + private.grossSales` reconciles to the
 * summary's `sales.grossSales`, and on the NHS side
 * `voucherValue + patientContribution` reconciles to `nhs.grossSales`.
 */
export type ISalesFunding = {
  period: ISalesPeriod;
  nhs: ISalesFundingSide & {
    /** Recovered from PCSE against the vouchers applied to these sales. */
    voucherValue: number;
    /** The excess these patients paid at the till on top of the voucher. */
    patientContribution: number;
  };
  private: ISalesFundingSide;
  bands: ISalesFundingBand[];
};

/** One product a member of staff dispensed, counted in units. */
export type ISalesStaffProduct = {
  productId: string;
  productName: string;
  sku: string | null;
  productType: string | null;
  quantity: number;
  saleCount: number;
};

/** What one member of staff dispensed in the period. */
export type ISalesStaffRow = {
  /** Null for dispenses with no creator recorded ("Unassigned"). */
  userId: number | null;
  userName: string;
  saleCount: number;
  spectaclesDispensed: number;
  /** Sale header value, VAT-inclusive — sums to the summary's grossSales. */
  grossSales: number;
  unitsSold: number;
  framesSold: number;
  lensesSold: number;
  products: ISalesStaffProduct[];
};

/** GET /dispense/sales/staff — sales per dispensing member of staff. */
export type ISalesStaff = {
  period: ISalesPeriod;
  totals: Omit<ISalesStaffRow, 'userId' | 'userName' | 'products'>;
  staff: ISalesStaffRow[];
};

export type ISalesGroup = {
  key: string | null;
  label: string;
  productType?: string;
  sku?: string;
  unitsSold: number;
  lineCount: number;
  saleCount: number;
  grossValue: number;
  netValue: number;
  vatAmount: number;
  exemptAmount: number;
  costValue: number;
  marginValue: number;
  /** Null when there is no apportioned revenue to measure against. */
  marginPercent: number | null;
  /** Lines with no VAT category yet, excluded from cost and margin. */
  linesUnapportioned: number;
};

export type ISalesBreakdown = {
  period: ISalesPeriod;
  groupBy: ISalesGroupBy;
  totals: {
    unitsSold: number;
    grossValue: number;
    netValue: number;
    vatAmount: number;
    exemptAmount: number;
    costValue: number;
    marginValue: number;
    linesUnapportioned: number;
  };
  groups: ISalesGroup[];
};

export type ISalesItem = {
  id: number;
  dispenseId: number;
  soldAt: string;
  dispenseStatus: string;
  patientId: string;
  productId: string;
  productName: string;
  sku: string | null;
  productType: string | null;
  vatCategory: string | null;
  batchNumber: string | null;
  quantity: number;
  unitPrice: number;
  lineGross: number;
  lineNet: number;
  lineVat: number;
  lineExempt: number;
  vatRateApplied: number;
  exemptRateApplied: number;
};

/** Item totals for every matching item in the period, not just one page. */
export type ISalesItemsTotals = {
  saleCount: number;
  itemCount: number;
  quantity: number;
  lineGross: number;
  lineVat: number;
  lineExempt: number;
};

/**
 * One sale's item lines summed (all of them, even those on another page),
 * beside the figures recorded on the dispense. Recorded values are null when
 * the sale predates VAT capture.
 */
export type ISaleItemsTotal = {
  dispenseId: number;
  itemCount: number;
  quantity: number;
  lineGross: number;
  lineVat: number;
  lineExempt: number;
  recordedGross: number | null;
  recordedVat: number | null;
  recordedExempt: number | null;
};

export type ISalesItemsResponse = {
  period: ISalesPeriod;
  page: number;
  limit: number;
  totalRecords: number;
  /** Absent from API builds before per-sale subtotals. */
  totals?: ISalesItemsTotals;
  saleTotals?: ISaleItemsTotal[];
  data: ISalesItem[];
};

export type IDispensePayment = {
  id: number;
  dispenseId: number;
  patientId: string | null;
  tillId: number | null;
  amount: number;
  direction: 'payment' | 'refund';
  method: string | null;
  paidAt: string;
  narrative: string | null;
  refundReason: string | null;
  refundMode: string | null;
};

export type ISalesPaymentsResponse = {
  period: ISalesPeriod;
  page: number;
  limit: number;
  totalRecords: number;
  totals: ISalesPaymentTotals;
  byMethod: {
    method: string;
    direction: 'payment' | 'refund';
    count: number;
    total: number;
  }[];
  data: IDispensePayment[];
};

/** Product-level VAT breakdown for a single sale. */
export type ISaleVatLine = {
  lineId: number;
  productId: string;
  productName: string;
  sku: string | null;
  productType: string | null;
  vatCategory: string | null;
  quantity: number;
  unitPrice: number;
  lineGross: number;
  lineNet: number;
  lineVat: number;
  lineExempt: number;
  vatRateApplied: number;
  exemptRateApplied: number;
};

export type ISaleVatBreakdown = {
  dispenseId: number;
  soldAt: string;
  status: string;
  patientId: string;
  totals: {
    charged: number;
    netAmount: number;
    vatAmount: number;
    exemptAmount: number;
    apportionmentRateApplied: number;
  };
  lines: ISaleVatLine[];
  addonsLine: {
    label: string;
    lineGross: number;
    lineNet: number;
    lineVat: number;
    lineExempt: number;
  } | null;
  payments: {
    totalPaid: number;
    totalRefunded: number;
    outstanding: number;
    entries: {
      id: number;
      amount: number;
      direction: 'payment' | 'refund';
      method: string | null;
      paidAt: string;
    }[];
  };
};
